import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { formatCurrency, formatNumber } from "@/lib/formatters";
import { formatDate } from "@/lib/formatters";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  LayoutGrid,
  Package,
  ChevronRight,
  ReceiptText,
  Boxes,
  User,
  GraduationCap,
  Calendar,
  History,
  ArrowRight,
  FlaskConical,
  Loader2,
} from "lucide-react";
import { useMemo } from "react";
import { isScheduledOrderClientViewEnabled } from "@/features/scheduledOrderClientViewFeature";
import { isScheduledOrderStateBadgesEnabled } from "@/features/scheduledOrderStateBadgesFeature";
import { decodeSeries, mapEtapaToGradeName, mapEtapaToSegment } from "@/lib/utils";
import { clientsApi, productsApi, profileClientApi, scheduledOrdersApi } from "@/lib/api";
import { resolveCycleYearFromCode } from "@/lib/orderCycle";
import { splitOrderItemRows } from "@/lib/couponItems";
import { isCouponEnabled } from "@/features/couponFeature";
import { useQuery } from "@tanstack/react-query";
import { isNewScheduledOrderCycleEnabledForCycle } from "@/features/newScheduledOrderCycleFeature";
import { Link, useLocation } from "react-router-dom";

type StatusUpdate = {
  action: string;
  createdBy: string;
  createdAt: string;
  comment: string;
}
interface ScheduledOrderDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  clientId: string | null;
  clientName: string | null;
  orderNumber: string | null;
  orderPayload?: any | null;
  clientCode: string | null;
  cycleCode?: string | number | null;
  scheduledOrderId?: string | null;
}

export function ScheduledOrderDetailsModal({
  isOpen,
  onClose,
  clientId,
  clientName,
  clientCode,
  orderNumber,
  orderPayload,
  cycleCode,
  scheduledOrderId,
}: ScheduledOrderDetailsModalProps) {
  const location = useLocation();
  const isBackofficeView = location.pathname.startsWith("/backoffice");
  const orderDetailsBasePath = isBackofficeView
    ? "/backoffice/pedidos"
    : "/customer-portal/pedidos";
  const cycleYear = resolveCycleYearFromCode(cycleCode);
  const showItemOrigin = isNewScheduledOrderCycleEnabledForCycle(cycleYear);

  const { data: normalizedItemsResponse, isLoading: isLoadingItems } = useQuery({
    queryKey: ["scheduled-order-items", scheduledOrderId],
    queryFn: () => scheduledOrdersApi.getItems(scheduledOrderId!),
    enabled: isOpen && showItemOrigin && !!scheduledOrderId,
  });

  const { data: scheduledOrderDetails } = useQuery({
    queryKey: ["scheduled-order-audit-details", scheduledOrderId],
    queryFn: () => scheduledOrdersApi.getById(scheduledOrderId!),
    enabled: isOpen && showItemOrigin && !!scheduledOrderId,
  });

  const { data: itemChangesResponse, isLoading: isLoadingItemChanges } = useQuery({
    queryKey: ["scheduled-order-item-changes", scheduledOrderId],
    queryFn: () => scheduledOrdersApi.getItemChanges(scheduledOrderId!),
    enabled: isOpen && showItemOrigin && !!scheduledOrderId,
  });

  const itemChanges = itemChangesResponse?.data ?? [];

  const changeLabels: Record<string, string> = {
    ITEM_ADDED: "Item incluído",
    QUANTITY_INCREASED: "Quantidade aumentada",
    QUANTITY_DECREASED: "Quantidade reduzida",
    ITEM_REMOVED: "Item removido",
    CONFIRMED: "Pedido confirmado",
    ORDER_FROZEN: "Pedido congelado",
    ORDER_PROCESSING_STARTED: "Processamento iniciado",
    ORDER_PROCESSED: "Pedido processado",
    ORDER_PROCESSING_FAILED: "Falha no processamento",
  };

  const { data: products, isLoading: isLoadingProducts } = useQuery({
    queryKey: ["products", cycleCode],
    queryFn: async () => {
      const products = await productsApi.getActive(resolveCycleYearFromCode(cycleCode));
      return products.data || [];
    }
  });

  const isLoadingOrderItems = isLoadingProducts || isLoadingItems;

  const { data: history } = useQuery({
    queryKey: ["history", orderNumber, clientId],
    queryFn: async () => {
      const { data } = await profileClientApi.getPrfileHistory(Number(clientId));
      const result = data.filter(item => item.productCode == clientCode).sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
      return result;
    },
    enabled: isOpen && !showItemOrigin && !!Number(clientId)
  });

  const legacyStatusUpdate: StatusUpdate | null = useMemo(() => {
    if (!history || history.length === 0) return null;

    const data: StatusUpdate[] = [];

    if (history.length > 0) {
      history.forEach((item, index) => {
        if (item?.comment) {
          const details = item?.comment?.details ? JSON.parse(item?.comment?.details) : null;
          const { action } = details || {};
          const updated_payload = typeof details?.updated_payload === 'object' ? details?.updated_payload : details?.updated_payload ? JSON.parse(details.updated_payload) : null;
          const createdBy = updated_payload?.CreatedBy || details.confirmed_by || item.createdBy || "Desconecido";
          const dto = {
            action,
            createdBy,
            createdAt: item.createdAt,
            comment: item.comment.content
          };
          if (action.includes('confirm_scheduled_order')) data.push(dto);
        }
      })
    }

    return data.length > 0 ? data[0] : null;
  }, [history]);

  const statusUpdate: StatusUpdate | null = useMemo(() => {
    if (!showItemOrigin) return legacyStatusUpdate;

    const status = scheduledOrderDetails?.status;
    const isConfirmedOrLater = status === "confirmed"
      || status === "processing"
      || status === "processed"
      || status === "completed"
      || status === "failed";
    if (!isConfirmedOrLater) return null;

    const confirmation = itemChanges
      .filter(change => change.changeType === "CONFIRMED")
      .sort((first, second) => new Date(second.changedAt).getTime() - new Date(first.changedAt).getTime())[0];
    if (!confirmation) return null;

    return {
      action: confirmation.changeType,
      createdBy: confirmation.changedBy || "Desconhecido",
      createdAt: confirmation.changedAt,
      comment: confirmation.reason || "Pedido programado confirmado",
    };
  }, [showItemOrigin, legacyStatusUpdate, scheduledOrderDetails?.status, itemChanges]);



  const groupedBySegment = useMemo(() => {
    let payload = orderPayload;

    if (!payload) return { Items: [] };
    if (typeof payload === "string") {
      try {
        payload = JSON.parse(payload);
      } catch (e) {
        console.error("Failed to parse orderPayload", e);
        return { Items: [] };
      }
    }

    const items = showItemOrigin
      ? (normalizedItemsResponse?.data ?? [])
      : payload?.Items || payload?.item || payload?.items || payload?.Item || [];

    if (!Array.isArray(items) || items.length === 0) return { Items: [] };
    if (!products) return { Items: [] };

    const mappedProduct = items.map(item => {
      const productCode = item.ProductCode ?? item.productCode;
      const productSku = item.ProductSku ?? item.productSku;
      const product = products.find(row => row.codigoProduto == productCode || row.sku == productSku);

      return ({
        productId: item.ProductId ?? item.productId,
        utilizacao: product?.utilizacao || "-",
        productCode,
        productSku,
        productDescription: item.ProductDescription ?? item.productDescription,
        seriesName: item.ProductSeries ?? item.productSeries ?? "-",
        segmentName: item.SegmentName ?? item.segmentName ?? product?.segmento,
        contractualQuantity: Number(item.QuantityOrdered ?? item.quantityOrdered ?? 0),
        bonifiedQuantity: Number(item.BonifiedQuantity ?? item.bonifiedQuantity ?? 0),
        paidQuantity: Math.max(
          Number(item.QuantityOrdered ?? item.quantityOrdered ?? 0)
          - Number(item.BonifiedQuantity ?? item.bonifiedQuantity ?? 0),
          0,
        ),
        productPrice: Number(item.NetPrice ?? item.netPrice ?? item.UnitPrice ?? item.unitPrice ?? 0),
        // Cupom por itens: unidades cobertas e desconto previsto (o total segue bruto; o abatimento é feito na cobrança).
        couponQuantity: Number(item.CouponQuantity ?? item.couponQuantity ?? 0),
        couponDiscountAmount: Number(item.CouponDiscountAmount ?? item.couponDiscountAmount ?? 0),
        couponCode: (item.CouponCode ?? item.couponCode ?? null) as string | null,
        totalWithDiscount: orderNumber.includes('PED-SIM')
          ? 0
          : Number(item.ItemTotal ?? item.itemTotal
            ?? (Math.max(
              Number(item.QuantityOrdered ?? item.quantityOrdered ?? 0)
              - Number(item.BonifiedQuantity ?? item.bonifiedQuantity ?? 0),
              0,
            ) * Number(item.NetPrice ?? item.netPrice ?? 0))),
      })
    });

    // Cada situação do item vira uma linha: sem cupom e uma por cupom. O item com cupom por itens se divide em unidades
    // pagas (sem cupom) e unidades cobertas pelo cupom; depois as linhas iguais, de pedidos de origem diferentes, se somam.
    const lines = splitOrderItemRows(mappedProduct.map(product => ({
      ...product,
      quantityOrdered: product.contractualQuantity,
      netPrice: product.productPrice,
      itemTotal: Math.max(Math.round((product.totalWithDiscount - (product.couponQuantity > 0 ? product.couponDiscountAmount : 0)) * 100) / 100, 0),
    }))).map(({ item, kind, quantity, total }) => {
      const isCoupon = kind === "coupon";
      return {
        ...item,
        lineCouponCode: isCoupon ? (item.couponCode || "Cupom") : "",
        contractualQuantity: quantity,
        bonifiedQuantity: isCoupon ? 0 : item.bonifiedQuantity,
        paidQuantity: isCoupon ? 0 : Math.max(quantity - item.bonifiedQuantity, 0),
        couponQuantity: isCoupon ? quantity : 0,
        couponDiscountAmount: isCoupon ? item.couponDiscountAmount : 0,
        totalWithDiscount: total,
      };
    });

    const consolidatedProducts = Array.from(lines.reduce((grouped, item) => {
      const key = JSON.stringify([
        item.lineCouponCode,
        item.productId ?? null,
        item.productSku ?? "",
        item.productCode ?? "",
        item.productDescription ?? "",
        item.seriesName ?? "",
        item.segmentName ?? "",
        item.utilizacao ?? "",
      ]);
      const existing = grouped.get(key);
      if (!existing) {
        grouped.set(key, { ...item });
        return grouped;
      }

      existing.contractualQuantity += item.contractualQuantity;
      existing.bonifiedQuantity += item.bonifiedQuantity;
      existing.paidQuantity += item.paidQuantity;
      existing.couponQuantity += item.couponQuantity;
      existing.couponDiscountAmount += item.couponDiscountAmount;
      existing.totalWithDiscount += item.totalWithDiscount;
      return grouped;
    }, new Map<string, typeof lines[number]>()).values());

    const segmentOrder: Record<string, number> = {
      "Ed. Infantil": 1,
      "Fund. Anos Iniciais": 2,
      "Fund. Anos Finais": 3,
      "Ensino Médio": 4,
      "PV": 5,
      "Outros": 999
    };

    consolidatedProducts.sort((a, b) => {
      const segmentA = a.segmentName || mapEtapaToSegment(a.seriesName);
      const segmentB = b.segmentName || mapEtapaToSegment(b.seriesName);

      const weightA = segmentOrder[segmentA] || 999;
      const weightB = segmentOrder[segmentB] || 999;

      if (weightA !== weightB) return weightA - weightB;

      const seriesA = mapEtapaToGradeName(a.seriesName);
      const seriesB = mapEtapaToGradeName(b.seriesName);
      const bySeries = (seriesA || "").localeCompare(seriesB || "");
      if (bySeries !== 0) return bySeries;
      // Mesmo produto: a linha sem cupom vem antes das linhas de cupom.
      return (a.productDescription || "").localeCompare(b.productDescription || "") || a.lineCouponCode.localeCompare(b.lineCouponCode);
    });

    return { ...payload, Items: consolidatedProducts };
  }, [orderPayload, products, normalizedItemsResponse, showItemOrigin]);

  // Cupom no pedido programado: por itens, as unidades cobertas saem em linha separada; por valor, só o desconto.
  // Um item por cupom: por itens, o desconto é a soma das linhas bonificadas do cupom; por valor, o valor do cupom.
  const couponSummaries = useMemo(() => {
    if (!isCouponEnabled || !showItemOrigin) return [] as Array<{ code: string; amount: number; byValue: boolean }>;
    const lineItems: any[] = groupedBySegment.Items ?? [];
    return (scheduledOrderDetails?.coupons ?? [])
      .map(coupon => {
        const byValue = coupon.discountType !== "Items";
        const itemsAmount = lineItems
          .filter(line => line.lineCouponCode && line.lineCouponCode === coupon.code)
          .reduce((sum, line) => sum + line.couponDiscountAmount, 0);
        return { code: coupon.code, byValue, amount: Math.round((byValue || itemsAmount <= 0 ? coupon.amount : itemsAmount) * 100) / 100 };
      })
      .filter(coupon => coupon.amount > 0);
  }, [scheduledOrderDetails?.coupons, groupedBySegment, showItemOrigin]);

  // As linhas bonificadas já saem líquidas; só o cupom por valor é abatido do total.
  const valueCouponTotal = couponSummaries.filter(coupon => coupon.byValue).reduce((sum, coupon) => sum + coupon.amount, 0);

  const totalOrderAmount = useMemo(() => {
    const lines = groupedBySegment.Items?.reduce((acc, group) => acc + group.totalWithDiscount, 0) ?? 0;
    return Math.max(Math.round((lines - valueCouponTotal) * 100) / 100, 0);
  }, [groupedBySegment, valueCouponTotal]);

  const renderProductTable = (products: any[]) => {
    return (
    <div className="rounded-lg border bg-background overflow-hidden shadow-sm">
      <Table>
        <TableHeader>
          <TableRow>
            {/* <TableHead scope="col">Código</TableHead> */}
            <TableHead scope="col">Descrição</TableHead>
            <TableHead scope="col" className="text-left">Segmento</TableHead>
            <TableHead scope="col" className="text-left">Série</TableHead>
            <TableHead scope="col" className="text-left">Tipo</TableHead>
            <TableHead scope="col" className="text-left">SKU</TableHead>

            <TableHead scope="col" className="text-left">Quantidade</TableHead>
            {!isScheduledOrderClientViewEnabled && (
              <>
                <TableHead scope="col" className="text-left">Bonificada</TableHead>
                <TableHead scope="col" className="text-left">Qtd. paga</TableHead>
              </>
            )}
            <TableHead scope="col" className="text-left">Valor Unit.</TableHead>
            <TableHead scope="col" className="text-left">Total</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {products.map((product, idx) => {
            const isCouponRow = !!product.lineCouponCode;
            const quantity = product.contractualQuantity;
            const total = product.totalWithDiscount;
            return (
            <TableRow
              key={`${product.productSku}-${idx}-${product.lineCouponCode}`}
              className={`${isCouponRow ? "bg-emerald-50/50" : ""} hover:bg-blue-50/30 transition-colors border-border last:border-0`}
            >
              {/* <TableCell className="font-mono text-sm">{product.productCode}</TableCell> */}
              <TableCell>
                <div className="flex flex-wrap items-center gap-2">
                  <span>{product.productDescription}</span>
                  {isCouponRow && (
                    <Badge variant="outline" className="border-emerald-300 bg-emerald-50 text-emerald-700 text-[10px]">
                      {product.lineCouponCode}
                    </Badge>
                  )}
                </div>
              </TableCell>
              <TableCell className="text-left">{product.segmentName || mapEtapaToSegment(product.seriesName)}</TableCell>
              <TableCell className="text-left">{mapEtapaToGradeName(product.seriesName)}</TableCell>
              <TableCell className="text-left">{product.utilizacao}</TableCell>
              <TableCell className="text-left">{product.productSku}</TableCell>

              <TableCell className="text-left">{quantity}</TableCell>
              {!isScheduledOrderClientViewEnabled && (
                <>
                  <TableCell className="text-left">{isCouponRow ? quantity : product.bonifiedQuantity}</TableCell>
                  <TableCell className="text-left">{isCouponRow ? 0 : Math.max(quantity - product.bonifiedQuantity, 0)}</TableCell>
                </>
              )}
              <TableCell className="text-left">{formatCurrency(product.productPrice)}</TableCell>
              <TableCell className="text-left font-medium">
                {formatCurrency(total)}
              </TableCell>
            </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className={`${showItemOrigin ? "max-w-6xl" : "max-w-5xl"} h-[90vh] flex flex-col p-0 overflow-hidden border-none shadow-2xl !translate-y-[-50%]`}>
        {/* Header Fixo */}
        <div className="bg-muted/50 px-6 py-5 border-b shrink-0">
          <div className="flex items-start gap-4">
            <div className="bg-blue-600 p-2.5 rounded-xl shadow-blue-200 shadow-lg">
              <ReceiptText className="h-6 w-6 text-white" />
            </div>
            <div className="space-y-1">
              <DialogTitle className="text-xl font-bold text-foreground tracking-tight">
                Detalhamento do Pedido Programado
              </DialogTitle>
              <div className="flex items-center">
                <div className="flex items-center gap-2 font-medium text-sm">
                  <span className="bg-blue-50 text-blue-700 border border-blue-100 px-2 py-0.5 rounded text-[11px] font-bold uppercase tracking-wider">
                    {orderNumber}
                  </span>
                  <ChevronRight className="h-4 w-4 text-muted-foreground/40" />
                  <span className="text-foreground font-semibold">{clientName}</span>
                </div>

                {isScheduledOrderStateBadgesEnabled && isBackofficeView && showItemOrigin && scheduledOrderDetails && (
                  <div className="flex flex-wrap items-center gap-2 ml-6">
                    {scheduledOrderDetails.isFrozen && (
                      <Badge className="bg-slate-800 text-white">Congelado</Badge>
                    )}
                    {scheduledOrderDetails.processedPurchaseOrderId && (
                      <Badge className="bg-blue-600 text-white">Processado</Badge>
                    )}
                    {scheduledOrderDetails.reconstructionStatus && scheduledOrderDetails.reconstructionStatus !== "NATIVE" && (
                      <Badge variant="secondary">Legado: {scheduledOrderDetails.reconstructionStatus}</Badge>
                    )}
                  </div>
                )}

                {!!statusUpdate && (
                  <div className="flex items-center gap-4 ml-8 pl-8 border-l border-muted-foreground/20">
                    <div className="flex flex-col gap-1">
                      <div className="flex items-center gap-2">
                        <Badge variant="secondary" className=" text-[10px] font-bold uppercase tracking-wider h-5 px-2">
                          Confirmado por
                        </Badge>
                      </div>
                      <div className="flex flex-col items-left gap-1 text-[15px] text-muted-foreground whitespace-nowrap">
                        {statusUpdate?.createdBy && (
                          <div className="flex items-center gap-1">
                            <User className="h-4 w-4" />
                            <span>{statusUpdate.createdBy}</span>
                          </div>
                        )}
                        <div className="flex items-center gap-1">
                          <Calendar className="h-4 w-4" />
                          <span>{formatDate(new Date(statusUpdate.createdAt))}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Área de Scroll */}
        <div className="flex-1 min-h-0 bg-white">
          <ScrollArea className="h-full">
            <div className="px-6 py-6 space-y-6">
              {/* {showItemOrigin && scheduledOrderDetails && (
                <section className="grid gap-3 rounded-xl border bg-muted/20 p-4 text-sm md:grid-cols-3">
                  <div>
                    <div className="text-xs text-muted-foreground">Data de corte</div>
                    <div className="font-medium">{scheduledOrderDetails.cutoffAt ? new Date( scheduledOrderDetails.cutoffAt ).toLocaleString( "pt-BR" ) : "Não configurada"}</div>
                  </div>
                  <div>
                    <div className="text-xs text-muted-foreground">Congelamento</div>
                    <div className="font-medium">{scheduledOrderDetails.frozenAt ? new Date( scheduledOrderDetails.frozenAt ).toLocaleString( "pt-BR" ) : "Ainda não congelado"}</div>
                    {scheduledOrderDetails.frozenBy && <div className="text-xs text-muted-foreground">por {scheduledOrderDetails.frozenBy}</div>}
                  </div>
                  <div>
                    <div className="text-xs text-muted-foreground">Pedido materializado</div>
                    {scheduledOrderDetails.processedPurchaseOrderId ? (
                      <Link className="font-medium text-blue-700 hover:underline" to={`${ orderDetailsBasePath }/${ scheduledOrderDetails.processedPurchaseOrderId }`}>
                        Abrir pedido processado
                      </Link>
                    ) : <div className="font-medium">Ainda não processado</div>}
                  </div>
                </section>
              )} */}
              {isLoadingOrderItems ? (
                <div role="status" className="flex flex-col items-center justify-center py-16 text-center">
                  <Loader2 className="h-10 w-10 animate-spin text-blue-600 mb-3" />
                  <p className="text-muted-foreground">Carregando itens do pedido...</p>
                </div>
              ) : groupedBySegment?.Items?.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 text-center border-2 border-dashed rounded-2xl bg-muted/50">
                  <Boxes className="h-12 w-12 text-muted-foreground/40 mb-3" />
                  <h3 className="text-lg font-bold text-foreground">Nenhum item encontrado</h3>
                  <p className="text-muted-foreground">Nenhum dado de produto disponível neste pedido agendado.</p>
                </div>
              ) : (
                <>
                  {renderProductTable(groupedBySegment.Items)}
                </>
              )}

              {/* {showItemOrigin && (
                <section className="rounded-xl border bg-background overflow-hidden shadow-sm" aria-labelledby="scheduled-order-history-title">
                  <div className="flex items-center justify-between gap-4 px-5 py-4 border-b bg-muted/30">
                    <div className="flex items-center gap-3">
                      <div className="rounded-lg bg-blue-50 p-2 text-blue-700">
                        <History className="h-5 w-5" />
                      </div>
                      <div>
                        <h3 id="scheduled-order-history-title" className="font-semibold text-foreground">Histórico de mudanças</h3>
                        <p className="text-xs text-muted-foreground">Alterações auditáveis realizadas nos itens deste pedido.</p>
                      </div>
                    </div>
                    <Badge variant="outline">{itemChanges.length} evento{itemChanges.length === 1 ? "" : "s"}</Badge>
                  </div>

                  <div className="divide-y">
                    {isLoadingItemChanges && (
                      <div className="px-5 py-8 text-center text-sm text-muted-foreground">Carregando histórico...</div>
                    )}
                    {!isLoadingItemChanges && itemChanges.length === 0 && (
                      <div className="px-5 py-8 text-center text-sm text-muted-foreground">
                        Nenhuma alteração auditada para este pedido.
                      </div>
                    )}
                    {itemChanges.map(change => {
                      let metadata: Record<string, unknown> = {};
                      try {
                        metadata = change.metadata ? JSON.parse(change.metadata) : {};
                      } catch {
                        metadata = {};
                      }
                      const isSynthetic = metadata.synthetic === true;

                      return (
                        <div key={change.id} className="px-5 py-4 flex items-start gap-4">
                          <div className="mt-1 h-2.5 w-2.5 rounded-full bg-blue-600 ring-4 ring-blue-50 shrink-0" />
                          <div className="min-w-0 flex-1 space-y-2">
                            <div className="flex flex-wrap items-center gap-2">
                              <span className="font-semibold text-sm">{changeLabels[change.changeType] ?? change.changeType}</span>
                              {isSynthetic && (
                                <Badge variant="secondary" className="gap-1 text-[10px]">
                                  <FlaskConical className="h-3 w-3" /> Teste
                                </Badge>
                              )}
                              <span className="text-xs text-muted-foreground">
                                {new Date(change.changedAt).toLocaleString("pt-BR")}
                              </span>
                            </div>
                            {(change.productDescription || change.productCode || change.productSku) && (
                              <div className="text-sm text-foreground">
                                <span className="font-medium">{change.productDescription || change.productCode}</span>
                                {change.productSku && <span className="ml-2 font-mono text-xs text-muted-foreground">SKU {change.productSku}</span>}
                              </div>
                            )}
                            <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                              {(change.previousQuantity != null || change.newQuantity != null) && (
                                <span className="inline-flex items-center gap-1">
                                  Quantidade: <strong className="text-foreground">{change.previousQuantity ?? "—"}</strong>
                                  <ArrowRight className="h-3 w-3" />
                                  <strong className="text-foreground">{change.newQuantity ?? "—"}</strong>
                                </span>
                              )}
                              {change.changedBy && <span>por {change.changedBy}</span>}
                              {change.sourcePurchaseOrderId && (
                                <Link
                                  to={`${orderDetailsBasePath}/${change.sourcePurchaseOrderId}`}
                                  className="font-medium text-blue-700 hover:underline"
                                >
                                  Pedido gerador: {change.sourceOrderNumber || change.sourcePurchaseOrderId}
                                </Link>
                              )}
                            </div>
                            {change.reason && <p className="text-xs text-muted-foreground">{change.reason}</p>}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </section>
              )} */}
            </div>
          </ScrollArea>
        </div>

        {/* Footer Fixo */}
        <div className="bg-foreground p-6 flex justify-between items-center shadow-inner relative overflow-hidden shrink-0">
          <div className="absolute top-0 right-0 -mt-12 -mr-12 w-48 h-48 bg-blue-500/10 rounded-full blur-3xl pointer-events-none" />

          <div className="flex items-center gap-3 z-10">
            <div className="bg-blue-500/20 p-2 rounded-lg border border-blue-500/30">
              <Boxes className="h-5 w-5 text-blue-400" />
            </div>
            <div>
              <p className="text-blue-300/60 text-[10px] font-bold uppercase tracking-[0.2em] leading-none mb-1">Total Consolidado</p>
              <h4 className="text-white font-medium">Itens de Pedido Programado</h4>
              {couponSummaries.map(coupon => (
                <p key={coupon.code} className="text-emerald-300 text-xs mt-0.5">Cupom {coupon.code}: − {formatCurrency(coupon.amount)}</p>
              ))}
            </div>
          </div>

          <div className="bg-white/5 border border-white/10 px-6 py-3 rounded-2xl flex items-baseline gap-3 z-10 backdrop-blur-sm">
            <span className="text-muted-foreground text-xs font-bold uppercase tracking-widest">Total</span>
            <span className="text-3xl font-bold text-white tabular-nums tracking-tight">
              {isLoadingOrderItems ? (
                <Loader2 className="h-7 w-7 animate-spin text-white/70" aria-label="Carregando total" />
              ) : (
                formatCurrency(totalOrderAmount)
              )}
            </span>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
